import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import StaffProfiles from '../StaffProfiles';
import Modal from '../Modal';
import Button from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { ScheduleChargesList } from '../SchedulePromptModal';
import type { ScheduleCharge } from '../../types/schedulePrompt';
import ModalBanner from '../ModalBanner';
import MarkdownRenderer from '../MarkdownRenderer';
import { seasonTermLabel } from '../../utils/seasonLabel';
import CourseRow from './CourseRow';
import CourseDetail from './CourseDetail';
import type { CourseMemberState } from './CourseRow';
import { hasPaidMembership, type IPricingSelection } from '../../utils/coursePricing';
import type { IActivityWithCourses, ICourse } from '../../utils/courseSchedule';
import './activityCard.css';
import { translateError } from '../../utils/errorMessages';

interface IUserMember {
  _id: string;
  name: string;
}

interface ActivityDetailModalProps {
  activity: IActivityWithCourses | null;
  onClose: () => void;
  /** Called after a successful join/leave so the parent can refetch. */
  onChanged?: () => void;
  /**
   * Externally controlled member id (dashboard mode). When provided together
   * with `hideMemberSelector`, the modal acts on this member and does not render
   * its own "Joining as" selector or fetch the user's members.
   */
  activeMemberId?: number | null;
  /** Hide the in-modal member selector and use `activeMemberId` instead. */
  hideMemberSelector?: boolean;
}

/**
 * Per-course enrollment state for the selected member, keyed by courseId.
 * Tracked locally so rows can update optimistically after a join/leave.
 */
type CourseStateMap = Record<number, CourseMemberState>;

const deriveState = (course: ICourse, memberId: number | null): CourseMemberState => {
  if (memberId == null) return 'none';
  if (course.enrolledMemberIds?.includes(memberId)) return 'enrolled';
  if (course.pendingMemberIds?.includes(memberId)) return 'pending';
  return 'none';
};

/**
 * Activity detail modal: a `ModalBanner` header (title + course count + season)
 * and an inline list of the activity's courses. Deliberately no cover image —
 * every course row carries its own, and a banner photo above them made two photo
 * layers compete for attention. Authenticated users pick a member once at the
 * top ("Joining as:") and join/leave each course from its row; logged-out users
 * see a read-only course list with a single login/register CTA.
 */
const ActivityDetailModal: React.FC<ActivityDetailModalProps> = ({
  activity,
  onClose,
  onChanged,
  activeMemberId = null,
  hideMemberSelector = false,
}) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [members, setMembers] = useState<IUserMember[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [submittingCourseId, setSubmittingCourseId] = useState<number | null>(null);
  const [stateMap, setStateMap] = useState<CourseStateMap>({});
  // Courses this member just joined that have paid membership options — drives
  // the informational "complete payment" banner (the Join → Pay bridge).
  const [payPrompts, setPayPrompts] = useState<Set<number>>(new Set());
  // When set, the modal renders the in-place course detail view instead of the grid.
  const [detailCourse, setDetailCourse] = useState<ICourse | null>(null);
  // Brief confirmation state for the copy-link button (icon flips to a check).
  const [linkCopied, setLinkCopied] = useState(false);
  // Leave asks first (D9 cascade); live charges are shown afterwards, never cancelled.
  const [leaveTarget, setLeaveTarget] = useState<ICourse | null>(null);
  const [leaveCharges, setLeaveCharges] = useState<ScheduleCharge[]>([]);

  // The Pay/Choose bridge only makes sense inside the member dashboard, where the
  // memberships page (choose-payment surface) exists. Controlled (dashboard) mode
  // is signalled by hideMemberSelector + activeMemberId.
  const inDashboard = hideMemberSelector;

  const goToPayment = useCallback(() => {
    onClose();
    navigate('/dashboard/memberships');
  }, [onClose, navigate]);

  // Start at the grid whenever the modal opens or switches to another activity,
  // and clear any pending pay prompts from a previous activity.
  // Depend on the activity IDENTITY (_id), not the prop object reference, so a
  // post-join parent refetch that swaps in a fresh selectedActivity object for
  // the same activity doesn't wipe the just-shown pay-prompt banner.
  useEffect(() => {
    setDetailCourse(null);
    setPayPrompts(new Set());
  }, [activity?._id]);

  // Fetch the user's members once when an authenticated modal opens. Skipped in
  // controlled (dashboard) mode, where the parent supplies the active member.
  useEffect(() => {
    if (hideMemberSelector) return;
    if (!activity || !isAuthenticated) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    api.my
      .members()
      .then((res) => {
        if (cancelled) return;
        const list = (res.data as IUserMember[]) || [];
        setMembers(list);
        setSelectedMemberId(list.length === 1 ? list[0]._id : '');
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activity, isAuthenticated, hideMemberSelector]);

  // In controlled mode the active member comes from the parent; otherwise it's
  // the modal's own selection.
  const selectedId = hideMemberSelector
    ? activeMemberId
    : selectedMemberId
    ? Number(selectedMemberId)
    : null;
  const selectedMemberName = members.find((m) => String(m._id) === String(selectedId))?.name ?? null;

  const courses = useMemo(
    () =>
      activity
        ? [...activity.courses].sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [activity]
  );

  // Derive per-course state for the selected member whenever the selection
  // or the activity payload changes.
  useEffect(() => {
    const map: CourseStateMap = {};
    for (const course of courses) {
      map[course._id] = deriveState(course, selectedId);
    }
    setStateMap(map);
    // Pay prompts are per just-joined member; reset when the member switches.
    setPayPrompts(new Set());
  }, [courses, selectedId]);

  // Facts worth stating before the course grid: how many courses, and the season
  // they run in. The season is taken from the first course that has a term —
  // courses of one activity share the active season in practice, and a course
  // with no term simply contributes nothing.
  const bannerMeta = useMemo(() => {
    if (!activity) return [];
    const pills: string[] = [];
    if (courses.length > 0) {
      pills.push(t('courses.countPill', { count: courses.length }));
    }
    const termed = courses.find((c) => c.termStart || c.termEnd);
    if (termed) {
      pills.push(seasonTermLabel(termed.termStart, termed.termEnd, t, i18n.language));
    }
    return pills;
  }, [activity, courses, t, i18n.language]);

  useEffect(() => {
    if (!linkCopied) return;
    const timer = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [linkCopied]);

  // Reset the confirmation when the modal switches activities.
  useEffect(() => {
    setLinkCopied(false);
  }, [activity?._id]);

  /**
   * Copies the current URL, which the parent page keeps in sync with the open
   * activity (`?activity=<id>`). Deliberately the current location rather than a
   * constructed public link: from the public page that yields a public link,
   * from the dashboard a dashboard one, and we never advertise a public URL for
   * an activity that may not be publicly visible.
   */
  const handleCopyLink = useCallback(async () => {
    // navigator.clipboard is undefined on insecure origins.
    if (!navigator.clipboard?.writeText) {
      addToast(t('activities.copyFailed'), 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      addToast(t('activities.linkCopied'), 'success');
    } catch {
      addToast(t('activities.copyFailed'), 'error');
    }
  }, [addToast, t]);

  if (!activity) return null;

  const description = activity.description?.trim() || '';

  const handleJoin = async (course: ICourse, selection?: IPricingSelection) => {
    if (!selectedId) return;
    setSubmittingCourseId(course._id);
    try {
      const { data } = await api.courses.join(course._id, selectedId);
      const result = data as { status?: string };
      if (result.status === 'pending_approval') {
        // Approval-policy: nothing is owed until an admin approves, so no payment
        // prompt — the existing pending state + notifications cover it. A stretch
        // picked in the pricing modal is intentionally NOT carried across: no
        // enrollment row exists yet, so assignment would 403. The member picks
        // again once approved.
        addToast(t('courses.requestSent'), 'info');
        setStateMap((prev) => ({ ...prev, [course._id]: 'pending' }));
      } else {
        setStateMap((prev) => ({ ...prev, [course._id]: 'enrolled' }));

        // Open enrollment: the member already chose a stretch in the pricing
        // modal, so assign it now rather than bouncing them to the payment page.
        let assigned = false;
        if (selection) {
          try {
            await api.my.chooseMembership(selectedId, {
              membershipId: selection.membershipId,
              seasonId: course.activeSeasonId ?? undefined,
              segmentScheme: selection.segmentScheme,
              segmentIndex: selection.segmentIndex,
            });
            assigned = true;
            addToast(t('courses.joinedAndChosen'), 'success');
          } catch (assignErr: any) {
            // The enrollment stands; only the assignment failed. Say so and fall
            // back to the Pay bridge instead of reporting a failed sign-up.
            addToast(
              translateError(assignErr, t, 'courses.joinedChoosePending'),
              'warning',
            );
          }
        }

        if (!assigned) {
          if (!selection) addToast(t('courses.joined'), 'success');
          // Open-enrollment join into a paid course → surface the Pay bridge.
          if (inDashboard && hasPaidMembership(course)) {
            setPayPrompts((prev) => new Set(prev).add(course._id));
          }
        }
      }
      onChanged?.();
    } catch (err: any) {
      addToast(translateError(err, t), 'error');
    } finally {
      setSubmittingCourseId(null);
    }
  };

  const handleLeave = async (course: ICourse) => {
    if (!selectedId) return;
    setSubmittingCourseId(course._id);
    try {
      const { data } = await api.courses.leave(course._id, selectedId);
      setLeaveTarget(null);
      addToast(t('courses.left'), 'success');
      if (data?.charges?.length) setLeaveCharges(data.charges);
      setStateMap((prev) => ({ ...prev, [course._id]: 'none' }));
      setPayPrompts((prev) => {
        if (!prev.has(course._id)) return prev;
        const next = new Set(prev);
        next.delete(course._id);
        return next;
      });
      onChanged?.();
    } catch (err: any) {
      addToast(translateError(err, t), 'error');
    } finally {
      setSubmittingCourseId(null);
    }
  };

  const renderMemberBar = () => {
    if (!isAuthenticated) {
      return (
        <div className="activity-detail__login">
          <span className="activity-detail__login-label">{t('courses.loginToJoin')}</span>
          <span className="activity-detail__login-cta-row">
            <Link to="/register" className="activity-detail__login-link">
              {t('welcomePage.register')}
            </Link>
            <Link to="/login" className="activity-detail__login-link">
              {t('welcomePage.login')}
            </Link>
          </span>
        </div>
      );
    }

    if (members.length === 0) {
      return (
        <p className="activity-detail__no-members">
          <Link to="/dashboard/members">{t('courses.noMembers')}</Link>
        </p>
      );
    }

    return (
      <div className="activity-detail__member-bar">
        <label className="activity-detail__member-label" htmlFor="activity-member-select">
          {t('courses.joiningAs')}
        </label>
        {members.length === 1 ? (
          <p className="activity-detail__member-name">{members[0].name}</p>
        ) : (
          <select
            id="activity-member-select"
            className="activity-detail__select"
            value={selectedMemberId}
            onChange={(e) => setSelectedMemberId(e.target.value)}
          >
            <option value="">--</option>
            {members.map((m) => (
              <option key={m._id} value={m._id}>
                {m.name}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={!!activity}
        onClose={onClose}
        title={activity.name}
        size="xlarge"
        banner={
          <ModalBanner
            title={activity.name}
            meta={bannerMeta}
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyLink}
                title={t('activities.copyLink')}
                aria-label={t('activities.copyLink')}
                iconLeft={<i className={linkCopied ? 'fas fa-check' : 'fas fa-link'} aria-hidden="true" />}
              >
                {linkCopied ? t('activities.linkCopied') : t('activities.copyLink')}
              </Button>
            }
          />
        }
      >
        <div className="activity-detail">
          <StaffProfiles people={activity.staff} />
          {description && (
            <div className="activity-detail__description">
              <MarkdownRenderer content={description} />
            </div>
          )}

          {!hideMemberSelector && renderMemberBar()}

          {courses.length > 0 ? (
            <div className="activity-detail__courses course-grid">
              {courses.map((course) => (
                <CourseRow
                  key={course._id}
                  course={course}
                  selectedMemberId={selectedId}
                  state={stateMap[course._id] ?? 'none'}
                  submitting={submittingCourseId === course._id}
                  onJoin={handleJoin}
                  onLeave={setLeaveTarget}
                  onOpenDetail={setDetailCourse}
                  showPayPrompt={payPrompts.has(course._id)}
                  onGoToPayment={inDashboard ? goToPayment : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="activity-detail__empty">{t('courses.noCoursesYet')}</p>
          )}
        </div>
      </Modal>

      {detailCourse && (
        <Modal
          isOpen={!!detailCourse}
          onClose={() => setDetailCourse(null)}
          title={detailCourse.name}
          size="large"
        >
          <CourseDetail
            course={detailCourse}
            selectedMemberId={selectedId}
            state={stateMap[detailCourse._id] ?? 'none'}
            submitting={submittingCourseId === detailCourse._id}
            onJoin={handleJoin}
            onLeave={setLeaveTarget}
            showPayPrompt={payPrompts.has(detailCourse._id)}
            onGoToPayment={inDashboard ? goToPayment : undefined}
          />
        </Modal>
      )}

      <ConfirmModal
        isOpen={!!leaveTarget}
        onClose={() => setLeaveTarget(null)}
        onConfirm={() => leaveTarget && handleLeave(leaveTarget)}
        title={t('courses.leaveConfirmTitle')}
        message={selectedMemberName
          ? t('courses.leaveConfirmMessage', { member: selectedMemberName, course: leaveTarget?.name ?? '' })
          : t('courses.leaveConfirmMessageSelf', { course: leaveTarget?.name ?? '' })}
        confirmText={t('courses.leave')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={leaveTarget != null && submittingCourseId === leaveTarget._id}
      />

      <Modal
        isOpen={leaveCharges.length > 0}
        onClose={() => setLeaveCharges([])}
        title={t('schedulePrompt.leftWithChargesTitle')}
        size="medium"
      >
        <div className="schedule-prompt">
          <ScheduleChargesList charges={leaveCharges} note={t('schedulePrompt.memberChargesNote')} />
          <div className="schedule-prompt__actions">
            <Button variant="primary" onClick={() => setLeaveCharges([])}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ActivityDetailModal;
